import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Logo } from "../../components/logo.js";

const canonicalUrl = "https://memokite.com/privacy";

export function PrivacyPage(): React.JSX.Element {
  useEffect(() => {
    document.title = "Privacy Policy · MemoKite";

    const existingCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const canonical = existingCanonical ?? document.createElement("link");
    const previousHref = existingCanonical?.href;
    canonical.rel = "canonical";
    canonical.href = canonicalUrl;
    if (!existingCanonical) document.head.append(canonical);

    return () => {
      if (existingCanonical && previousHref) {
        existingCanonical.href = previousHref;
      } else {
        canonical.remove();
      }
    };
  }, []);

  return (
    <main className="privacy-shell">
      <header className="privacy-header">
        <Link className="brand-link" to="/" aria-label="MemoKite home">
          <Logo />
        </Link>
        <Link className="privacy-header-link" to="/login">
          Log in
        </Link>
      </header>

      <article className="privacy-document">
        <p className="section-kicker">Your information</p>
        <h1>Privacy Policy</h1>
        <p className="privacy-effective-date">Effective 3 August 2026</p>

        <section aria-labelledby="privacy-overview">
          <h2 id="privacy-overview">Overview</h2>
          <p>
            This policy explains how KNOKODE LIMITED (“we”, “us”, or “our”) handles personal
            information when you use MemoKite, which KNOKODE LIMITED created, operates and provides.
            KNOKODE LIMITED is the controller of that information and decides how account email
            addresses are used. MemoKite is the service, not a separate company or recipient of your
            email address. MemoKite is offered worldwide, so your local law may give you additional
            rights.
          </p>
          <p>
            This policy describes MemoKite’s current practices and clearly identifies features that
            are not yet active. It does not promise that the same law or right applies in every
            country.
          </p>
          <address className="privacy-contact-card">
            <strong>KNOKODE LIMITED</strong>
            <span>4 Vicarage Road</span>
            <span>Teddington, England, TW11 8EZ</span>
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a>
          </address>
        </section>

        <section aria-labelledby="privacy-collect">
          <h2 id="privacy-collect">Information we collect</h2>
          <p>Depending on how you use MemoKite, we process:</p>
          <ul>
            <li>
              <strong>Account information:</strong> email address, a password hash, display name,
              timezone, account and email-verification status, and related timestamps. We do not
              store your password in plain text.
            </li>
            <li>
              <strong>Authentication and security information:</strong> verification records,
              session identifiers, rate-limit records, IP-derived hashed identifiers, request
              identifiers, and security and diagnostic logs.
            </li>
            <li>
              <strong>Your learning content:</strong> collections, activities, saved links, rich
              text, notes, uploads, filenames and file metadata.
            </li>
            <li>
              <strong>Learning and planning information:</strong> schedules, completion records,
              ratings, favourites, and learning history.
            </li>
            <li>
              <strong>Support information:</strong> messages and other information you send when
              asking us for help or making a privacy request.
            </li>
            <li>
              <strong>Google Sign-In information:</strong> the account-linking information described
              in “Google Sign-In” below, when that option is available and you choose it.
            </li>
          </ul>
        </section>

        <section aria-labelledby="privacy-sources">
          <h2 id="privacy-sources">Where information comes from</h2>
          <p>
            We receive information directly from you, generate information as you use MemoKite, and
            receive limited identity information from Google if you choose Google Sign-In. When you
            ask MemoKite to inspect a saved link, we may also retrieve basic metadata, such as a
            page title or preview information, from that external website.
          </p>
          <p>
            MemoKite does not currently use PostHog or another analytics or behavioural-tracking
            service.
          </p>
        </section>

        <section aria-labelledby="privacy-use">
          <h2 id="privacy-use">How and why we use information</h2>
          <p>We use personal information to:</p>
          <ul>
            <li>create and operate accounts and provide MemoKite’s features;</li>
            <li>authenticate users, maintain sessions, and safely link sign-in methods;</li>
            <li>
              verify email addresses and send MemoKite account and security notices, support
              replies, important information about the service or changes to the Terms or this
              Privacy Policy, and other non-promotional administrative messages needed to provide
              MemoKite;
            </li>
            <li>store and display the learning content you choose to add;</li>
            <li>
              prevent abuse, protect MemoKite and its users, troubleshoot, and improve reliability;
            </li>
            <li>comply with law and respond to valid privacy requests.</li>
          </ul>
          <p>
            Under UK and European data-protection law, our reasons for this processing are that it
            is necessary to provide MemoKite to you, necessary for our legitimate interests in
            protecting the service and its users, or required by law. If we rely on consent for a
            future activity, you may withdraw that consent at any time without affecting earlier
            processing.
          </p>
          <p>
            These necessary service communications are administrative, not marketing, and do not
            indicate that you have consented to marketing. We do not currently use your account
            email address to advertise other services provided by KNOKODE LIMITED. Before doing so,
            we would ask for your separate, optional permission. Declining or withdrawing that
            permission would not affect your ability to sign up for or use MemoKite, or stop
            necessary service messages.
          </p>
          <p>
            Accepting the Terms, acknowledging this Privacy Policy, signing up by email or Google
            Sign-In, remaining silent, or continuing to use MemoKite does not give marketing
            permission. Any future permission would cover only KNOKODE LIMITED’s own services, not
            services from affiliates, partners or another company.
          </p>
        </section>

        <section aria-labelledby="privacy-google">
          <h2 id="privacy-google">Google Sign-In</h2>
          <p>
            When Google Sign-In is configured and available, MemoKite uses it only to authenticate
            you. Google sends MemoKite a signed ID token. MemoKite verifies that token and processes
            its issuer, Google’s stable account identifier, verified email address, optional name,
            optional hosted-domain information, and account-linking information.
          </p>
          <p>
            We use the stable identifier and email to create, locate, or safely link your MemoKite
            account. A Google name may become a new account’s initial display name. Email
            verification and hosted-domain information are used only to make account linking safe. A
            profile-picture URL may be present in the token, but MemoKite does not use or store it.
          </p>
          <p>
            We retain the Google issuer, stable identifier, email, link status, and related
            timestamps until the MemoKite account is deleted. The raw ID token is used transiently
            for verification and is not stored. MemoKite does not receive or store a Google access
            token or refresh token.
          </p>
          <p>
            MemoKite does not access Gmail, Drive, Calendar, Classroom, other Google content, or
            other Google APIs. We do not sell Google-derived information or use it for targeted or
            personalised advertising.
          </p>
        </section>

        <section aria-labelledby="privacy-cookies">
          <h2 id="privacy-cookies">Cookies and analytics</h2>
          <p>
            MemoKite currently uses only an essential session cookie. It keeps you signed in and
            helps protect access to your account. MemoKite does not currently use analytics,
            behavioural tracking, targeted-advertising cookies, or personalised advertising.
          </p>
          <p>
            We will update this policy and, where required, request consent before introducing
            optional analytics such as PostHog.
          </p>
        </section>

        <section aria-labelledby="privacy-sharing">
          <h2 id="privacy-sharing">Who receives information</h2>
          <p>
            KNOKODE LIMITED does not sell or rent account email addresses or disclose them to other
            companies for those companies’ own marketing or independent commercial purposes. The
            following limited service providers process information on KNOKODE LIMITED’s behalf
            where necessary to operate MemoKite:
          </p>
          <ul>
            <li>
              <strong>Fly.io</strong> for application hosting and request processing;
            </li>
            <li>
              <strong>Neon</strong> to store account information, including email addresses, in the
              PostgreSQL database;
            </li>
            <li>
              <strong>Cloudflare R2</strong> for private file storage; and
            </li>
            <li>
              <strong>Mailgun</strong> to receive email addresses and deliver verification and other
              service emails.
            </li>
          </ul>
          <p>
            We may also disclose information when required by law, to answer valid rights requests,
            to protect MemoKite or its users, or as part of a proposed or completed corporate
            transaction, subject to appropriate protections. We do not sell personal information or
            share it for cross-context behavioural or targeted advertising.
          </p>
        </section>

        <section aria-labelledby="privacy-transfers">
          <h2 id="privacy-transfers">International processing</h2>
          <p>
            MemoKite is offered worldwide and information may be processed in the United States and
            other countries where we or our service providers operate. Those countries may have
            different data-protection laws from your country. Contact us if you would like
            information about safeguards relevant to your data.
          </p>
        </section>

        <section aria-labelledby="privacy-retention">
          <h2 id="privacy-retention">Retention and deletion</h2>
          <p>
            We generally keep account information and user content while your account is active. To
            request deletion of your account and personal information, email{" "}
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a>. After we
            approve a request, we remove account data from live systems promptly, except where
            limited information must be retained for legal or security reasons.
          </p>
          <p>
            Backup copies are isolated from ordinary use and overwritten within 30 days. If a backup
            is restored, we reapply completed deletion requests. Stored Google account linkage is
            deleted with the MemoKite account; the raw Google ID token is not retained.
          </p>
        </section>

        <section aria-labelledby="privacy-rights">
          <h2 id="privacy-rights">Your privacy rights</h2>
          <p>
            Depending on where you live and subject to legal exceptions, you may have the right to
            ask us to provide or give you access to information, correct it, delete it, restrict its
            use, object to its use, or provide a portable copy. Where processing relies on consent,
            you may withdraw it. These rights are not absolute and do not apply identically
            worldwide.
          </p>
          <p>
            Send a request to{" "}
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a>. We may
            ask for information reasonably needed to verify your identity. We will not discriminate
            against you for exercising an applicable United States privacy right.
          </p>
          <p>
            You may complain to the UK Information Commissioner’s Office or, if applicable, the
            data-protection authority where you live or work in the EEA. We would appreciate the
            opportunity to address your concern first.
          </p>
        </section>

        <section aria-labelledby="privacy-us">
          <h2 id="privacy-us">United States disclosures</h2>
          <p>
            The categories of personal information we collect are identifiers and account details,
            internet or electronic activity and security information, user-provided content and
            files, learning and preference information, and support communications. We receive them
            from you, your use of MemoKite, Google when you choose Google Sign-In, and external
            websites when you ask us to inspect a saved link.
          </p>
          <p>
            We use these categories for the purposes described in “How and why we use information”
            and disclose them to the service providers and other qualified recipients described in
            “Who receives information”. We do not sell personal information, share it for
            cross-context behavioural or targeted advertising, or use it for personalised ads. You
            may make an applicable request using the contact method in “Your privacy rights”.
          </p>
        </section>

        <section aria-labelledby="privacy-age">
          <h2 id="privacy-age">Age requirement</h2>
          <p>
            MemoKite is for people aged 16 or older. MemoKite does not currently verify age or
            obtain parental consent. If you believe we hold information about someone under 16,
            please email{" "}
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a> so we can
            investigate and take appropriate action.
          </p>
        </section>

        <section aria-labelledby="privacy-automation">
          <h2 id="privacy-automation">Automated decisions and advertising</h2>
          <p>
            MemoKite does not currently make decisions based solely on automated processing that
            have legal or similarly significant effects. It does not provide personalised
            advertising or use PostHog for profiling.
          </p>
        </section>

        <section aria-labelledby="privacy-security">
          <h2 id="privacy-security">Security</h2>
          <p>
            We use reasonable technical and organisational measures intended to protect personal
            information. These include password hashing, private file storage, secure production
            session cookies, access controls, and log redaction. No system can guarantee absolute
            security.
          </p>
        </section>

        <section aria-labelledby="privacy-updates">
          <h2 id="privacy-updates">Changes to this policy</h2>
          <p>
            We may update this policy as MemoKite changes. We will update the effective date and
            provide additional notice when legally required. We will update the disclosure and
            obtain any required consent before introducing PostHog or materially expanding how
            MemoKite accesses Google information.
          </p>
        </section>

        <section aria-labelledby="privacy-contact">
          <h2 id="privacy-contact">Contact us</h2>
          <p>
            For questions or privacy requests, contact{" "}
            <a href="mailto:memokite-support@knokode.com">memokite-support@knokode.com</a> or write
            to KNOKODE LIMITED, 4 Vicarage Road, Teddington, England, TW11 8EZ.
          </p>
        </section>

        <p className="privacy-related-link">
          Read the <Link to="/terms">MemoKite Terms of Service</Link>.
        </p>
      </article>
    </main>
  );
}
